import React from 'react';
import { LotDistribution } from './data/ratioStats';
import Tooltip from './Ratio_Tooltip';

interface RatioRangeBarProps {
  dist: LotDistribution;
  surface?: number;
  compact?: boolean;
  ratioSuffix?: string;
}

const fmt = (v: number) => v.toLocaleString('fr-FR');
const fmtK = (v: number) => {
  if (v >= 1_000_000) return `${(v / 1_000_000).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} M€`;
  if (v >= 1_000) return `${Math.round(v / 1_000).toLocaleString('fr-FR')} k€`;
  return `${Math.round(v).toLocaleString('fr-FR')} €`;
};

// Couleur selon la variabilité du lot (coefficient de variation)
const iqrBg    = (cv: number) => cv < 0.2 ? 'bg-green-200'   : cv < 0.4 ? 'bg-violet-200'   : 'bg-orange-200';
const medColor = (cv: number) => cv < 0.2 ? 'bg-green-600'   : cv < 0.4 ? 'bg-violet-600'   : 'bg-orange-500';
const medText  = (cv: number) => cv < 0.2 ? 'text-green-700' : cv < 0.4 ? 'text-violet-700' : 'text-orange-600';

const cvLabel  = (cv: number) => cv < 0.2 ? 'stable — prix prévisibles d\'un projet à l\'autre'
                                : cv < 0.4 ? 'modérément variable — un cadrage soigné reste efficace'
                                :            'très variable — fort risque de dérive, priorité à la négociation';

const barTooltip = (dist: LotDistribution) =>
  `Bande colorée = zone où se situent 50 % des projets comparables (entre le bas et le haut de la fourchette courante).\nTrait vertical = valeur médiane — le coût le plus fréquent.\n\nCouleur de la bande :\n  🟢 vert  = lot stable (prix prévisible)\n  🟣 violet = variabilité normale\n  🟠 orange = lot très variable\n\nCe lot : ${cvLabel(dist.cv)} (CV = ${(dist.cv * 100).toFixed(0)} %).\n\nAttention : l'échelle est propre à chaque lot — ne pas comparer les barres entre lots.`;

const RatioRangeBar: React.FC<RatioRangeBarProps> = ({ dist, surface, compact = false, ratioSuffix = '€/m²' }) => {
  if (dist.nbProjets < 2) {
    return (
      <div className="text-xs text-gray-400 italic">
        Données insuffisantes ({dist.nbProjets} projet{dist.nbProjets !== 1 ? 's' : ''})
      </div>
    );
  }

  const span = dist.max - dist.min;
  const toPercent = (v: number) =>
    span > 0 ? Math.min(100, Math.max(0, ((v - dist.min) / span) * 100)) : 50;

  const p25pct = toPercent(dist.p25);
  const medpct = toPercent(dist.median);
  const p75pct = toPercent(dist.p75);
  // ── Mode compact (ensembles / sous-ensembles) ──
  if (compact) {
    return (
      <div className="space-y-1.5">
        {/* Fourchette Bas · Médiane · Haut */}
        <div className="flex items-center justify-between text-xs">
          <span>
            <span className="text-gray-400 mr-1">Bas</span>
            <span className="font-medium text-gray-700">{fmt(dist.p25)}</span>
          </span>
          <span className={`font-bold ${medText(dist.cv)}`}>
            méd. {fmt(dist.median)} <span className="font-normal text-gray-400">{ratioSuffix}</span>
          </span>
          <span>
            <span className="font-medium text-gray-700">{fmt(dist.p75)}</span>
            <span className="text-gray-400 ml-1">Haut</span>
          </span>
        </div>

        {/* Extrêmes */}
        <div className="flex justify-between text-xs text-gray-400">
          <span>min : {fmt(dist.min)}</span>
          <span className="text-gray-300">{ratioSuffix}</span>
          <span>max : {fmt(dist.max)}</span>
        </div>

        {/* Mini barre avec tooltip explicatif */}
        <Tooltip text={barTooltip(dist)} position="top" width="w-80" as="div">
          <div className="relative h-2 w-full bg-gray-200 rounded-full cursor-help">
            <div
              className={`absolute top-0 h-2 ${iqrBg(dist.cv)} rounded-full`}
              style={{ left: `${p25pct}%`, width: `${Math.max(p75pct - p25pct, 3)}%` }}
            />
            <div
              className={`absolute w-1 h-4 -top-1 ${medColor(dist.cv)} rounded-full`}
              style={{ left: `${medpct}%`, transform: 'translateX(-50%)' }}
            />
          </div>
        </Tooltip>

        {/* Projection si surface */}
        {surface !== undefined && surface > 0 && (
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-400">{fmtK(dist.p25 * surface)}</span>
            <span className={`font-semibold ${medText(dist.cv)}`}>
              {fmtK(dist.median * surface)}
            </span>
            <span className="text-gray-400">{fmtK(dist.p75 * surface)}</span>
          </div>
        )}

      </div>
    );
  }

  // ── Mode normal (carte principale) ──
  return (
    <div className="space-y-1.5">
      {/* Fourchette Bas · Médiane · Haut */}
      <div className="flex items-center justify-between text-xs">
        <span>
          <span className="text-gray-400 mr-1">Bas</span>
          <span className="font-medium text-gray-700">{fmt(dist.p25)}</span>
        </span>
        <span className={`font-bold ${medText(dist.cv)}`}>
          méd. {fmt(dist.median)} <span className="font-normal text-gray-400">€/m²</span>
        </span>
        <span>
          <span className="font-medium text-gray-700">{fmt(dist.p75)}</span>
          <span className="text-gray-400 ml-1">Haut</span>
        </span>
      </div>

      {/* Extrêmes */}
      <div className="flex justify-between text-xs text-gray-400">
        <span>min : {fmt(dist.min)}</span>
        <span className="text-gray-300">€/m²</span>
        <span>max : {fmt(dist.max)}</span>
      </div>

      {/* Barre avec tooltip */}
      <Tooltip text={barTooltip(dist)} position="top" width="w-80" as="div">
        <div className="relative h-2 w-full bg-gray-200 rounded-full cursor-help">
          <div
            className={`absolute top-0 h-2 ${iqrBg(dist.cv)} rounded-full`}
            style={{ left: `${p25pct}%`, width: `${Math.max(p75pct - p25pct, 2)}%` }}
          />
          <div
            className={`absolute w-1 h-4 -top-1 ${medColor(dist.cv)} rounded-full`}
            style={{ left: `${medpct}%`, transform: 'translateX(-50%)' }}
          />
        </div>
      </Tooltip>

      {/* Projection si surface */}
      {surface !== undefined && surface > 0 && (
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-400">{fmtK(dist.p25 * surface)}</span>
          <span className={`font-semibold ${medText(dist.cv)}`}>
            {fmtK(dist.median * surface)}
          </span>
          <span className="text-gray-400">{fmtK(dist.p75 * surface)}</span>
        </div>
      )}
    </div>
  );
};

export default RatioRangeBar;
